import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { filter, map, take } from 'rxjs/operators';
import { AuthService } from '@app/core/services/auth.service';

/**
 * Cookie-based AuthGuard (CW-13d-client §3.4).
 *
 * Drives on the `currentUser` signal populated by `AuthService.restoreSession()`
 * (called from the service constructor on app boot). Handles the page-
 * refresh race where the signal is null while `GET /auth/me` is in-flight
 * by waiting on the `restoreInProgress` signal.
 *
 * On unauthenticated access: redirect to `/auth/login` with the attempted
 * URL preserved via `?returnUrl=...` so the login flow can return the user
 * to the intended destination.
 */
export const authGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const redirectToLogin = (): UrlTree =>
    router.createUrlTree(['/auth/login'], {
      queryParams: { returnUrl: state.url },
    });

  // Synchronously allow if the user is already loaded.
  if (authService.isAuthenticated()) {
    return true;
  }

  // Page-refresh race: GET /auth/me is in-flight. Wait for it.
  if (authService.restoreInProgress()) {
    return toObservable(authService.restoreInProgress).pipe(
      filter((inProgress) => !inProgress),
      take(1),
      map(() => (authService.isAuthenticated() ? true : redirectToLogin())),
    );
  }

  // No user, no restore in flight — bounce to login.
  return redirectToLogin();
};
